import { AppBook } from '../types/utils';

export interface Page {
    page_id: string;
    chapter_id: string;
    questions: any[] | string;
}

export interface QuestionCounts {
  questionSets: number;
  individualQuestions: number;
}

export interface ChapterQuestionCounts {
  [chapterId: string]: QuestionCounts;
}

export class QuestionCounter {
  /**
   * Count individual questions for a specific chapter using AppBook structure
   */
  static countQuestionsForChapter(book: AppBook, chapterId: string): number {
    const chapter = book.chapters[chapterId];
    if (!chapter) return 0;
    
    return chapter.trueFalseQuestions.length + chapter.multipleChoiceQuestions.length;
  }

  /**
   * Count true/false questions for a specific chapter
   */
  static countTrueFalseQuestionsForChapter(book: AppBook, chapterId: string): number {
    const chapter = book.chapters[chapterId];
    if (!chapter) return 0;
    
    return chapter.trueFalseQuestions.length;
  }

  /**
   * Count multiple choice questions for a specific chapter
   */
  static countMultipleChoiceQuestionsForChapter(book: AppBook, chapterId: string): number {
    const chapter = book.chapters[chapterId];
    if (!chapter) return 0;
    
    return chapter.multipleChoiceQuestions.length;
  }

  /**
   * Get total question counts for the entire book
   */
  static getTotalBookCounts(book: AppBook): { trueFalse: number; multipleChoice: number; total: number } {
    let trueFalse = 0;
    let multipleChoice = 0;
    
    Object.values(book.chapters).forEach(chapter => {
      trueFalse += chapter.trueFalseQuestions.length;
      multipleChoice += chapter.multipleChoiceQuestions.length;
    });
    
    return {
      trueFalse,
      multipleChoice,
      total: trueFalse + multipleChoice
    };
  }

  /**
   * Get question counts for all chapters in the book
   */
  static getAllChapterCountsFromBook(book: AppBook): ChapterQuestionCounts {
    const counts: ChapterQuestionCounts = {};
    
    Object.entries(book.chapters).forEach(([chapterId, chapter]) => {
      counts[chapterId] = {
        questionSets: 1, // Each chapter is one "set" in the new structure
        individualQuestions: chapter.trueFalseQuestions.length + chapter.multipleChoiceQuestions.length
      };
    });
    
    return counts;
  }
  /**
   * Count question sets (pages) for a specific chapter
   */
  static countQuestionSetsForChapter(
    trueFalseQuestions: Page[], 
    multipleChoiceQuestions: Page[], 
    chapterId: string
  ): number {
    const trueFalseCount = trueFalseQuestions.filter(q => q.chapter_id === chapterId).length;
    const multipleChoiceCount = multipleChoiceQuestions.filter(q => q.chapter_id === chapterId).length;
    return trueFalseCount + multipleChoiceCount;
  }

  /**
   * Count individual questions for a specific chapter
   */
  static countIndividualQuestionsForChapter(
    trueFalseQuestions: Page[], 
    multipleChoiceQuestions: Page[], 
    chapterId: string
  ): number {
    const filteredTrueFalseQuestions = trueFalseQuestions.filter(q => q.chapter_id === chapterId);
    const multipleChoiceQuestionSets = multipleChoiceQuestions.filter(q => q.chapter_id === chapterId);
    
    const trueFalseCount = filteredTrueFalseQuestions.reduce((total: number, questionSet: Page) => {
      return total + (Array.isArray(questionSet.questions) ? questionSet.questions.length : 0);
    }, 0);
    
    const multipleChoiceCount = multipleChoiceQuestionSets.reduce((total: number, questionSet: Page) => {
      return total + (Array.isArray(questionSet.questions) ? questionSet.questions.length : 0);
    }, 0);
    
    return trueFalseCount + multipleChoiceCount;
  }

  /**
   * Get both question set and individual question counts for a chapter
   */
  static getChapterCounts(
    trueFalseQuestions: Page[], 
    multipleChoiceQuestions: Page[], 
    chapterId: string
  ): QuestionCounts {
    return {
      questionSets: this.countQuestionSetsForChapter(trueFalseQuestions, multipleChoiceQuestions, chapterId),
      individualQuestions: this.countIndividualQuestionsForChapter(trueFalseQuestions, multipleChoiceQuestions, chapterId)
    };
  }

  /**
   * Get counts for all chapters
   */
  static getAllChapterCounts(
    trueFalseQuestions: Page[], 
    multipleChoiceQuestions: Page[], 
    availableChapters: string[]
  ): ChapterQuestionCounts {
    const counts: ChapterQuestionCounts = {};
    
    for (const chapterId of availableChapters) {
      counts[chapterId] = this.getChapterCounts(trueFalseQuestions, multipleChoiceQuestions, chapterId);
    }
    
    return counts;
  }

  /**
   * Get total counts across all chapters
   */
  static getTotalCounts(
    trueFalseQuestions: Page[], 
    multipleChoiceQuestions: Page[], 
    availableChapters: string[]
  ): QuestionCounts {
    const chapterCounts = this.getAllChapterCounts(trueFalseQuestions, multipleChoiceQuestions, availableChapters);
    
    const totalQuestionSets = Object.values(chapterCounts).reduce((sum, counts) => sum + counts.questionSets, 0);
    const totalIndividualQuestions = Object.values(chapterCounts).reduce((sum, counts) => sum + counts.individualQuestions, 0);
    
    return {
      questionSets: totalQuestionSets,
      individualQuestions: totalIndividualQuestions
    };
  }

  /**
   * Count total question sets (for backward compatibility)
   */
  static countTotalQuestionSets(trueFalseQuestions: Page[], multipleChoiceQuestions: Page[]): number {
    return trueFalseQuestions.length + multipleChoiceQuestions.length;
  }

  /**
   * Count total individual questions
   */
  static countTotalIndividualQuestions(trueFalseQuestions: Page[], multipleChoiceQuestions: Page[]): number {
    const trueFalseCount = trueFalseQuestions.reduce((total, questionSet) => {
      return total + (Array.isArray(questionSet.questions) ? questionSet.questions.length : 0);
    }, 0);
    
    const multipleChoiceCount = multipleChoiceQuestions.reduce((total, questionSet) => {
      return total + (Array.isArray(questionSet.questions) ? questionSet.questions.length : 0);
    }, 0);
    
    return trueFalseCount + multipleChoiceCount;
  }
} 